import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "./ui/sonner";

/**
 * Mandatory legal-acceptance gatekeeper.
 *
 * Renders nothing while the auth state is loading.
 * If the logged-in user has not accepted the legal disclaimer, this modal
 * blocks access to the rest of the application until they click the button.
 *
 * - Cannot be closed by clicking outside, pressing Escape, or any X button.
 * - On confirm, persists `has_accepted_legal: true` on the user profile.
 */
export default function LegalGatekeeperModal() {
  const { user, setUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  // Art. 49(1)(a) GDPR explicit consent state. Must be checked before the
  // "I understand and accept" button is clickable — this is what turns the
  // single tap into "explicit, informed, freely given" consent for the
  // international transfer to non-EU processors (Emergent / OpenAI). The
  // server records this together with `has_accepted_legal: true`.
  const [transferConsent, setTransferConsent] = useState(false);

  // Only show for authenticated users who haven't yet accepted.
  // Crucially, we don't render anything while auth is still loading
  // (user === null) — would create an FOUC-like flash on every reload.
  const shouldShow = !!user && user !== false && user.has_accepted_legal !== true;

  const handleAccept = async () => {
    if (submitting || !transferConsent) return;
    setSubmitting(true);
    try {
      await api.post("/auth/accept-legal");
      // Optimistically update the local user so the modal hides immediately
      setUser?.((curr) => (curr && curr !== false ? { ...curr, has_accepted_legal: true } : curr));
    } catch (err) {
      toast.error("Could not record your acceptance", { description: err.response?.data?.detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="legal-gatekeeper"
          // Block clicking through to anything underneath
          onClick={(e) => e.stopPropagation()}
        >
          {/* Backdrop — opaque, no dismiss-on-click */}
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-lg bg-[#0A0A0A] border border-white/[0.12] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="legal-gatekeeper-title"
          >
            {/* Header with shield mark */}
            <div className="flex items-start gap-4 px-7 pt-7 pb-5 border-b border-white/[0.10]">
              <div className="w-10 h-10 flex items-center justify-center bg-[#D4AF37]/[0.06] border border-[#D4AF37]/25 flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/65 mb-1">
                  Welcome to EQUIA
                </p>
                <h2
                  id="legal-gatekeeper-title"
                  className="font-serif text-2xl text-white leading-tight"
                >
                  Data Processing &amp; Accuracy Disclaimer
                </h2>
              </div>
            </div>

            {/* Body */}
            <div className="px-7 py-6 space-y-4">
              <p className="text-white/90 text-sm leading-relaxed">
                By using EQUIA, you acknowledge that your documents and financial
                data are processed using <span className="text-[#D4AF37]">Artificial Intelligence</span>.
                While we strive for 100% accuracy, all calculated values, yields,
                and legal roadmaps are estimates and for{" "}
                <span className="text-white">informational purposes only</span>.
              </p>

              <p className="text-white/80 text-xs leading-relaxed">
                Please review our{" "}
                <Link
                  to="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4AF37] hover:text-[#E8C757] underline-offset-2 hover:underline transition-colors"
                >
                  Privacy Policy
                </Link>{" "}
                for more details on how your data is collected, processed, and stored.
              </p>

              <div className="flex items-start gap-2.5 bg-amber-500/[0.06] border border-amber-500/20 px-3 py-2.5 mt-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-[11px] leading-relaxed text-amber-100/75">
                  EQUIA is <span className="font-medium text-amber-200">not a substitute</span>{" "}
                  for legal, tax or investment advice. Always consult qualified
                  professionals before making decisions based on the data shown.
                </p>
              </div>

              {/* International transfer disclosure — Art. 49(1)(a) GDPR
                  explicit consent. The checkbox below records that the user
                  was specifically informed of the non-EU processing locations
                  and chose to proceed. */}
              <div className="bg-white/[0.03] border border-white/[0.10] px-4 py-3 mt-3 space-y-2">
                <p className="text-[11px] tracking-[0.15em] uppercase text-white/55">
                  Cross-border processing notice
                </p>
                <p className="text-[12px] leading-relaxed text-white/80">
                  Your stored data (account, portfolio, encrypted documents)
                  remains in <strong className="text-white/95">EU data centres</strong> (Frankfurt).
                  However, our hosting partner <strong className="text-white/95">Emergent Agent</strong>{" "}
                  currently operates application servers in the{" "}
                  <strong className="text-white/95">United States</strong>, and AI
                  inference is performed by <strong className="text-white/95">OpenAI L.L.C.</strong>{" "}
                  (United States). Both transfers are necessary to provide the
                  service.
                </p>
                <label className="flex items-start gap-3 cursor-pointer pt-1" data-testid="transfer-consent-label">
                  <input
                    type="checkbox"
                    checked={transferConsent}
                    onChange={(e) => setTransferConsent(e.target.checked)}
                    data-testid="transfer-consent-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                  />
                  <span className="text-[12px] leading-relaxed text-white/85">
                    I explicitly consent to my data being processed via
                    infrastructure outside the European Union as described
                    above and in the Privacy Policy
                    <span className="text-white/55"> (Art. 49(1)(a) GDPR)</span>.
                  </span>
                </label>
              </div>
            </div>

            {/* CTA */}
            <div className="px-7 py-5 border-t border-white/[0.10] bg-black/30">
              <button
                onClick={handleAccept}
                disabled={submitting || !transferConsent}
                data-testid="accept-legal-btn"
                className="w-full bg-[#D4AF37] text-black px-5 py-3 text-sm font-medium tracking-wide hover:bg-[#B5952F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Saving…" : "I understand and accept"}
              </button>
              <p className="text-[10px] text-white/60 text-center mt-3 tracking-wider uppercase">
                You must accept to continue
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
